import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

export default function Contact() {
  const { t, link } = useLang();
  const cp = (k) => t(`contactPage.${k}`);

  const rows = [
    { icon: MapPin, label: cp("addressLabel"), content: <>{cp("address")}<br />{cp("city")}</>, testid: "contact-address" },
    { icon: Phone, label: cp("phoneLabel"), content: <a href="tel:+306989164257" className="hover:text-electric transition-colors">{cp("phone")}</a>, testid: "contact-phone" },
    { icon: Mail, label: cp("emailLabel"), content: <a href="mailto:info@digital-company.gr" className="hover:text-electric transition-colors break-all">{cp("email")}</a>, testid: "contact-email" },
  ];

  return (
    <>
      <Seo page="contact" />
      <section className="bg-ink bg-grid-dark pt-[140px] pb-20 lg:pt-[180px] lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.25em] text-electric">{cp("eyebrow")}</p>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{cp("title")}</h1>
              <p className="mt-6 max-w-md text-base sm:text-lg text-steel leading-relaxed">{cp("sub")}</p>

              <div className="mt-10 rounded-2xl border border-white/10 bg-ink2/70 p-7 sm:p-8 backdrop-blur">
                <div className="space-y-7">
                  {rows.map((r) => (
                    <div key={r.label} className="flex items-start gap-4" data-testid={r.testid}>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-dblue to-electric">
                        <r.icon className="h-5 w-5 text-white" />
                      </span>
                      <div>
                        <p className="text-[10px] font-bold tracking-[0.25em] text-steel">{r.label}</p>
                        <p className="mt-1.5 font-display text-lg font-bold text-white">{r.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <p className="mt-8 text-sm text-steel">
                {cp("formNote")}{" "}
                <Link to={`${link("/")}#quote`} data-testid="contact-form-link" className="inline-flex items-center gap-1.5 font-semibold text-electric hover:gap-2.5 transition-all">
                  {cp("formLink")} <ArrowRight className="h-4 w-4" />
                </Link>
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                <img src="/images/data-flow.jpg" alt="Digital data network visual" className="w-full object-cover max-h-[300px]" width="1600" height="1067" />
              </div>
              <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                <iframe
                  title={cp("mapTitle")}
                  data-testid="contact-map"
                  src="https://maps.google.com/maps?q=%CE%9A%CE%B1%CF%81%CF%84%CE%B5%CF%81%CE%BF%CF%8D%2058%2C%20%CE%97%CF%81%CE%AC%CE%BA%CE%BB%CE%B5%CE%B9%CE%BF%2C%20%CE%9A%CF%81%CE%AE%CF%84%CE%B7&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="h-[320px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
          <p className="mt-14 font-display text-lg font-bold text-gradient">«{t("tagline")}»</p>
        </div>
      </section>
    </>
  );
}
